import React, { memo, useState, useEffect, useCallback, useMemo, useRef } from "react";
import styles from "./index.modules.less";
import classnames from "classnames";
import { Badge, Card, Form, Input, Col, Row, Select, DatePicker, Button, Table, Space, Empty, Pagination, Spin } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  adaptComplaintListResponse,
  buildComplaintListParams,
  getComplaintListDisplay
} from "./adapter";
import { getComplaintsListV2 } from "../../service/complaint";
import { isMobileDevice } from "../../utils/util";

const { RangePicker } = DatePicker;

const STATUS_TABS = [
  { key: 0, label: "全部投诉", showBadge: false },
  { key: 1, label: "待处理", showBadge: true },
  { key: 2, label: "处理中", showBadge: true },
  { key: 3, label: "处理完成", showBadge: false }
];

const PLATFORM_OPTIONS = [
  { value: "", label: "全部渠道" },
  { value: "0", label: "电话渠道" },
  { value: "3", label: "微信" },
  { value: "4", label: "支付宝" }
];

const MOBILE_STATUS_CLASS = {
  1: "statusPending",
  2: "statusProcessing",
  3: "statusDone"
};

const getDefaultComplaintDateRange = () => [
  dayjs().subtract(1, "year").startOf("day"),
  dayjs().endOf("day")
];

const buildComplaintCountParams = (baseOptions = {}, currStatus) => {
  return buildComplaintListParams({
    ...baseOptions,
    current: 1,
    pageSize: 1,
    currStatus
  });
};

const getListTotal = (res = {}) => Number(res.total_num) || 0;

const buildBaseLoadOptions = (formValues, pageOptions = {}) => ({
  orderNo: (formValues && formValues.orderNo) || "",
  phoneNo: (formValues && formValues.phoneNo) || "",
  platform: (formValues && formValues.platform !== undefined) ? formValues.platform : "",
  dateRange: (formValues && formValues.dateRange) || [],
  current: pageOptions.current || 1,
  pageSize: pageOptions.pageSize || 10
});

const NewComplaint = () => {
  const nav = useNavigate();
  const listTopRef = useRef(null);
  const [form] = Form.useForm();
  const [currentTab, setCurrentTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [processingCount, setProcessingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandRows, setExpandRows] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => window.innerWidth <= 768);
  const isMobile = isMobileDevice() || isNarrowViewport;

  useEffect(() => {
    const syncViewport = () => setIsNarrowViewport(window.innerWidth <= 768);
    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const initialFormValues = useMemo(
    () => ({
      orderNo: "",
      phoneNo: "",
      platform: "",
      dateRange: getDefaultComplaintDateRange()
    }),
    []
  );

  const doLoadData = useCallback(async (formValues, tab, pageOptions = {}) => {
    const baseOptions = buildBaseLoadOptions(formValues, pageOptions);
    const { current, pageSize } = baseOptions;

    const listParams = buildComplaintListParams({ ...baseOptions, currStatus: tab });

    setLoading(true);
    try {
      const listRes = await getComplaintsListV2(listParams).catch(() => ({}));

      const { list, total } = adaptComplaintListResponse(listRes || {});
      setPagination({
        current,
        pageSize
      });

      setTableData(list);
      setTotalCount(total);
    } catch (_err) {
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshComplaintCounts = useCallback(async (formValues, pageOptions = {}) => {
    const baseOptions = buildBaseLoadOptions(formValues, pageOptions);
    const pendingParams = buildComplaintCountParams(baseOptions, 1);
    const processingParams = buildComplaintCountParams(baseOptions, 2);

    try {
      const [pendingRes, processingRes] = await Promise.all([
        getComplaintsListV2(pendingParams).catch(() => ({})),
        getComplaintsListV2(processingParams).catch(() => ({}))
      ]);
      setPendingCount(getListTotal(pendingRes));
      setProcessingCount(getListTotal(processingRes));
    } catch (_err) {
      setPendingCount(0);
      setProcessingCount(0);
    }
  }, []);

  useEffect(() => {
    refreshComplaintCounts(initialFormValues);
    doLoadData(initialFormValues, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    const pageOptions = { current: 1, pageSize: pagination.pageSize };
    refreshComplaintCounts(formValues, pageOptions);
    doLoadData(formValues, currentTab, pageOptions);
  };

  const handleReset = () => {
    const nextValues = {
      ...initialFormValues,
      dateRange: getDefaultComplaintDateRange()
    };
    form.setFieldsValue(nextValues);
    setCurrentTab(0);
    const pageOptions = { current: 1, pageSize: pagination.pageSize };
    refreshComplaintCounts(nextValues, pageOptions);
    doLoadData(nextValues, 0, pageOptions);
  };

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    const formValues = form.getFieldsValue();
    const pageOptions = { current: 1, pageSize: pagination.pageSize };
    doLoadData(formValues, tab, pageOptions);
  };

  const scrollToListTop = () => {
    const node = listTopRef.current;
    if (!node) return;
    const scrollParent = document.getElementById("app");
    if (scrollParent && scrollParent.scrollHeight > scrollParent.clientHeight) {
      const top = node.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top + scrollParent.scrollTop;
      scrollParent.scrollTo({ top, behavior: "auto" });
      return;
    }
    node.scrollIntoView({ block: "start" });
  };

  const handleTableChange = (nextPagination) => {
    const formValues = form.getFieldsValue();
    doLoadData(formValues, currentTab, {
      current: nextPagination.current,
      pageSize: nextPagination.pageSize
    });
    scrollToListTop();
  };

  const toggleExpand = (rowKey) => {
    if (isMobile) {
      setExpandRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
    }
  };

  const handleMouseEnter = (rowKey) => {
    if (!isMobile) {
      setExpandRows(prev => ({ ...prev, [rowKey]: true }));
    }
  };

  const handleMouseLeave = (rowKey) => {
    if (!isMobile) {
      setExpandRows(prev => ({ ...prev, [rowKey]: false }));
    }
  };

  const gotoComplainDetail = (record) => {
    nav(`/view/newComplaintDetail/${record.complainNo}`);
  };

  const renderOrderInfo = (record) => {
    const { complainNo: rowKey, firstOrder, orderList } = record;
    const isExpanded = expandRows[rowKey];
    const display = getComplaintListDisplay(record);

    return (
      <div
        className={styles.orderIdList}
        onMouseEnter={() => handleMouseEnter(rowKey)}
        onMouseLeave={() => handleMouseLeave(rowKey)}
      >
        <div className={styles.firstOrder}>
          <div>流水：{firstOrder ? firstOrder.bankTradeCode : "--"}</div>
          <div>订单：{firstOrder ? firstOrder.orderId : "--"}</div>
        </div>
        {display.isExpandable && (
          <div
            className={styles.showMore}
            onClick={() => toggleExpand(rowKey)}
          >
            等 {orderList.length - 1} 笔订单
          </div>
        )}
        {display.isExpandable && (
          <div className={classnames(styles.subMenu, { [styles.show]: isExpanded })}>
            {orderList.slice(1).map((item, index) => (
              <div key={index} className={styles.orderItem}>
                <div>流水：{item.bankTradeCode}</div>
                <div>订单：{item.orderId}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: "关联流水/订单信息",
      key: "orderInfo",
      render: (_, record) => renderOrderInfo(record)
    },
    {
      title: "商品名称",
      key: "productName",
      render: (_, record) => {
        const display = getComplaintListDisplay(record);
        return <span>{display.productNameText}</span>;
      }
    },
    {
      title: "涉及总额",
      key: "totalAmount",
      render: (_, record) => (
        <div className={styles.active}>￥{(record.totalAmount || 0).toFixed(2)}</div>
      )
    },
    {
      title: "联系方式",
      key: "phoneNo",
      dataIndex: "phoneNo"
    },
    {
      title: "投诉内容",
      key: "complainContent",
      dataIndex: "complainContent"
    },
    {
      title: "订单时间",
      key: "payTime",
      render: (_, record) => (record.firstOrder ? record.firstOrder.payTime : "--")
    },
    {
      title: "投诉时间",
      dataIndex: "complainTime",
      key: "complainTime"
    },
    {
      title: "投诉来源",
      key: "platform",
      render: (_, record) => (
        <div className={styles.tags}>
          <div className={styles.tagIcon}></div>
          <div>{record.platformText}</div>
        </div>
      )
    },
    {
      title: "状态",
      key: "currStatus",
      render: (_, record) => <span>{record.listStatusText || record.currStatusText}</span>
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => {
        const display = getComplaintListDisplay(record);
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => gotoComplainDetail(record)}>
              {display.actionText}
            </Button>
          </Space>
        );
      }
    }
  ];

  const fieldSpan = isMobile ? 24 : 6;

  return (
      <div className={classnames(styles.complaintWrapper, { [styles.isMobile]: isMobile })}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            initialValues={initialFormValues}
            className={styles.searchForm}
          >
            <Row gutter={16}>
              <Col span={fieldSpan}>
                <Form.Item label="流水号查询" name="orderNo">
                  <Input placeholder="流水号" />
                </Form.Item>
              </Col>
              <Col span={fieldSpan}>
                <Form.Item label="联系方式" name="phoneNo">
                  <Input placeholder="用户手机号" />
                </Form.Item>
              </Col>
              <Col span={fieldSpan}>
                <Form.Item label="投诉来源" name="platform">
                  <Select options={PLATFORM_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={fieldSpan}>
                <Form.Item label="投诉时间范围" name="dateRange">
                  <RangePicker
                    className={styles.rangePicker}
                    popupClassName="complaint-range-popup"
                    inputReadOnly={isMobile}
                    placement="bottomLeft"
                    placeholder={["开始日期", "结束日期"]}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row align="middle" gutter={[0, 12]}>
              <Col span={isMobile ? 24 : 8}>
                <div>共查询到 <strong>{totalCount}</strong> 条数据</div>
              </Col>
              <Col span={isMobile ? 24 : 8} offset={isMobile ? 0 : 8}>
                <div className={styles.btnGroup}>
                  <Button onClick={handleReset}>重置</Button>
                  <Button className={styles.btnSearchStyle} type="primary" onClick={handleSearch}>
                    搜索
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>

        <div ref={listTopRef}>
        <Card className={styles.complaintMain}>
          <div className={styles.complaintMainTabs}>
            {STATUS_TABS.map(tab => (
              <div
                key={tab.key}
                className={classnames(styles.complaintMainTabsItem, { [styles.active]: currentTab === tab.key })}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}{tab.key === 1 && <Badge className={styles.redTooltip} count={pendingCount} />}
                {tab.key === 2 && <Badge className={styles.redTooltip} count={processingCount} />}
              </div>
            ))}
          </div>

          {isMobile ? (
            <div className={styles.mobileList}>
              <Spin spinning={loading}>
                {tableData.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无投诉数据" />
                ) : (
                  tableData.map(record => {
                    const display = getComplaintListDisplay(record);
                    const statusClass = styles[MOBILE_STATUS_CLASS[record.listStatus]] || "";
                    return (
                      <div key={record.complainNo} className={styles.mobileCard}>
                        <div className={styles.mobileCardHead}>
                          <div className={styles.mobileProduct}>{display.productNameText}</div>
                          <div className={classnames(styles.mobileStatus, statusClass)}>
                            {record.listStatusText || record.currStatusText}
                          </div>
                        </div>
                        <div className={classnames(styles.active, styles.mobileAmount)}>
                          ￥{(record.totalAmount || 0).toFixed(2)}
                        </div>
                        {renderOrderInfo(record)}
                        <div className={styles.mobileContent}>{record.complainContent || "--"}</div>
                        <div className={styles.mobileRow}>
                          <span className={styles.label}>联系方式</span>
                          <span className={styles.value}>{record.phoneNo || "--"}</span>
                        </div>
                        <div className={styles.mobileRow}>
                          <span className={styles.label}>订单时间</span>
                          <span className={styles.value}>{record.firstOrder ? record.firstOrder.payTime : "--"}</span>
                        </div>
                        <div className={styles.mobileRow}>
                          <span className={styles.label}>投诉时间</span>
                          <span className={styles.value}>{record.complainTime || "--"}</span>
                        </div>
                        <div className={styles.mobileRow}>
                          <span className={styles.label}>投诉来源</span>
                          <span className={styles.value}>{record.platformText || "--"}</span>
                        </div>
                        <Button
                          block
                          className={styles.mobileAction}
                          type="primary"
                          onClick={() => gotoComplainDetail(record)}
                        >
                          {display.actionText}
                        </Button>
                      </div>
                    );
                  })
                )}
              </Spin>
              {totalCount > pagination.pageSize && (
                <div className={styles.mobilePagination}>
                  <Pagination
                    simple
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={totalCount}
                    onChange={(current, pageSize) => handleTableChange({ current, pageSize })}
                  />
                </div>
              )}
            </div>
          ) : (
          <Table
            columns={columns}
            dataSource={tableData}
            rowKey="complainNo"
            loading={loading}
            rowClassName={() => styles.tableRow}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalCount,
              showSizeChanger: false
            }}
            onChange={handleTableChange}
          />
          )}
        </Card>
        </div>
      </div>
  );
};

export default memo(NewComplaint);
